// hooks/checkout-stage/useOrderDetails.ts
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../../store/slices/currentUserSlice";
import {
  selectIsExecutor,
  selectClientUserId,
} from "../../../../store/slices/executorSlice";
import { useInput } from "../../../hooks/useInput";
import { useValidation } from "../../../hooks/useValidation";
import {
  ValidationRulesInterface,
  ValidationResultInterface,
} from "../../../../common/types/types";

const MAX_COMMENT_LENGTH = 200;

const orderDetailsValidations: ValidationRulesInterface[] = [
  {
    fieldName: "phone",
    minLength: 10,
    maxLength: 15,
    canBeEmpty: true,
    isEmptyMessage: "Телефон обязателен",
    toShortMessage: "Слишком короткий номер",
    tooLongMessage: "Слишком длинный номер",
    format: /^\+?\d+$/,
    formatMessage: "Только цифры и + в начале",
  },
  {
    fieldName: "comment",
    maxLength: MAX_COMMENT_LENGTH,
    canBeEmpty: true,
    tooLongMessage: `Максимум ${MAX_COMMENT_LENGTH} символов`,
    isEmptyMessage: "",
    toShortMessage: "",
    formatMessage: "",
  },
];

export const useOrderDetails = () => {
  const currentUser = useSelector(selectCurrentUser);
  const isExecutor = useSelector(selectIsExecutor);
  const clientUserId = useSelector(selectClientUserId);
  const isAnonymousMode = isExecutor && !clientUserId;

  const [data, setData] = useState({
    phone: isAnonymousMode ? "" : currentUser?.phone_number || "",
    comment: "",
  });

  const [validationResult, setValidationResult] = useState<
    ValidationResultInterface[]
  >([]);
  const [isValid, setIsValid] = useState(false);

  const { inputHandler } = useInput(setData);
  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    orderDetailsValidations,
  );

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((item) => item.name === fieldName);
  };

  useEffect(() => {
    Object.keys(data).forEach((field) => {
      if (orderDetailsValidations.some((v) => v.fieldName === field)) {
        checkAndAddValidation(field);
      }
    });
  }, [checkAndAddValidation, data]);

  useEffect(() => {
    Object.entries(data).forEach(([field, value]) => {
      if (orderDetailsValidations.some((v) => v.fieldName === field)) {
        validate(field, value);
      }
    });
  }, [data]);

  useEffect(() => {
    const allValid = validationResult.every((item) => {
      if (isAnonymousMode && item.name === "phone") return true;
      return !item.notValid;
    });
    setIsValid(allValid);
  }, [validationResult, isAnonymousMode]);

  return {
    phone: data.phone,
    comment: data.comment,
    getValidationResult,
    inputHandler,
    setDirty,
    isValid,
    MAX_COMMENT_LENGTH,
  };
};
